import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

const ThemeContext = createContext({ isDark: true, toggleTheme: () => {} });

const DARK_VARS = {
  "--c-bg": "#000",
  "--c-bg2": "#0d0d0d",
  "--c-border": "#1a1a1a",
  "--c-border2": "#2a2a2a",
  "--c-text": "rgba(255,255,255,0.82)",
  "--c-text-dim": "rgba(255,255,255,0.4)",
  "--c-text-muted": "rgba(255,255,255,0.6)",
  "--c-heading": "#ffffff",
  "--c-heading-muted": "rgba(255,255,255,0.7)",
  "--c-chip-alt": "rgba(255,255,255,0.35)",
  "--c-chip-border": "#333333",
  "--c-code-bg": "#0a0a0a",
  "--c-code-border": "#333333",
  "--c-inline-code-bg": "#1a1a1a",
  "--c-blockquote-bg": "#0d0d0d",
  "--c-footer-link": "rgba(255,255,255,0.5)",
  "--c-table-row-hover": "#0a0a0a",
  "--c-tr-bg": "#0d0d0d",
};

const LIGHT_VARS = {
  "--c-bg": "#f8f8f8",
  "--c-bg2": "#eeeeee",
  "--c-border": "#dddddd",
  "--c-border2": "#cccccc",
  "--c-text": "rgba(0,0,0,0.82)",
  "--c-text-dim": "rgba(0,0,0,0.4)",
  "--c-text-muted": "rgba(0,0,0,0.6)",
  "--c-heading": "#111111",
  "--c-heading-muted": "rgba(0,0,0,0.7)",
  "--c-chip-alt": "rgba(0,0,0,0.35)",
  "--c-chip-border": "#bbbbbb",
  "--c-code-bg": "#1e1e1e",
  "--c-code-border": "#444444",
  "--c-inline-code-bg": "#efefef",
  "--c-blockquote-bg": "#f0f0f0",
  "--c-footer-link": "rgba(0,0,0,0.5)",
  "--c-table-row-hover": "#e8e8e8",
  "--c-tr-bg": "#f0f0f0",
};

function applyVars(vars) {
  Object.entries(vars).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value);
  });
}

export function ThemeContextProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("site-theme");
    return saved !== null ? saved === "dark" : true;
  });

  useEffect(() => {
    applyVars(isDark ? DARK_VARS : LIGHT_VARS);
    localStorage.setItem("site-theme", isDark ? "dark" : "light");
    // Global transition for smooth switch
    document.documentElement.style.setProperty(
      "--theme-transition",
      "background 0.25s ease, color 0.25s ease, border-color 0.25s ease"
    );
  }, [isDark]);

  // Apply immediately on mount (no flash)
  useEffect(() => {
    applyVars(isDark ? DARK_VARS : LIGHT_VARS);
  }, []);

  const toggleTheme = () => setIsDark((prev) => !prev);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

ThemeContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAppTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;